import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import Header from '../components/Header'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'

/**
 * #1189 Phase 3: Stripe LPM (Alipay / WeChat Pay) 経由のチャージ画面。
 *
 * bank_transfer_jp (Phase 2) と違い、redirect 系決済のため flow は:
 *   1. `/create-intent` mode=local_payment で PI 発行 (client_secret 取得)
 *   2. Stripe Elements の PaymentElement を描画、Alipay/WeChat Pay を選択させる
 *   3. `stripe.confirmPayment` で method の redirect 先へ遷移
 *   4. 支払完了 → `return_url` (=/charge/return?charge_id=...) 経由で戻る
 *   5. ChargeReturn 側で status 確認 → success 画面へ
 *
 * 訪日客の実利: Alipay + WeChat Pay で中国訪日客カバー (JPY 建て)。
 * 欧州/東南アジア客の LPM (iDEAL/FPX 等) は自国通貨のみのため Phase 3 スコープ外
 * (multi-currency 実装は将来別 issue)。
 */

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
)

type CreateIntentResponse = {
  charge_id: string
  amount_jpy: number
  fee_jpy: number
  total_jpy: number
  payment_intent_client_secret?: string | null
  payment_intent_id?: string | null
  local_payment_methods?: string[] | null
}

function PaymentForm({
  chargeId,
  totalJpy,
  amountJpy,
  feeJpy,
}: {
  chargeId: string
  totalJpy: number
  amountJpy: number
  feeJpy: number
}) {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useI18n()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)

    const returnUrl = `${window.location.origin}/charge/return?charge_id=${encodeURIComponent(chargeId)}`
    // Alipay / WeChat Pay は confirmPayment で対応 method の redirect URL に飛ばす。
    // 3D secure 系と違い、if_required では処理されない (常に redirect 必要)。
    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    })

    // ここに到達するのは confirmPayment がエラーで停止した場合のみ。
    // redirect が始まった時点で JS 実行は打ち切られる。
    if (stripeError) {
      setError(stripeError.message || t('chargeLocal.errorConfirm'))
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-4">
      <PaymentElement
        options={{
          layout: 'accordion',
          // 対応 method は backend が payment_method_types で指定済み
        }}
      />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="font-body text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="rounded-lg bg-gray-50 p-3 text-xs text-text-secondary">
        <div className="flex justify-between">
          <span>{t('chargeLocal.chargeAmount')}</span>
          <span>&yen; {amountJpy.toLocaleString()}</span>
        </div>
        <div className="mt-1 flex justify-between">
          <span>{t('chargeLocal.fee')}</span>
          <span>&yen; {feeJpy.toLocaleString()}</span>
        </div>
        <div className="mt-1 flex justify-between border-t border-gray-200 pt-1 font-semibold text-text-primary">
          <span>{t('chargeLocal.total')}</span>
          <span>&yen; {totalJpy.toLocaleString()}</span>
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || submitting}
        className="tap-effect h-14 w-full rounded-xl bg-yokoso-red font-display text-base font-semibold text-white shadow-button disabled:opacity-50"
      >
        {submitting
          ? t('chargeLocal.submitting')
          : t('chargeLocal.payCta', { amount: totalJpy.toLocaleString() })}
      </button>
    </form>
  )
}

export default function ChargeLocal() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useI18n()

  const state = (location.state ?? {}) as { amount?: number }
  const [loading, setLoading] = useState(true)
  const [intent, setIntent] = useState<CreateIntentResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  // ChargeBank と同じ理由で React StrictMode double-fire を ref で防ぐ。
  // Stripe PaymentIntent は confirm=false で発行するため orphan の実害は
  // bank_transfer_jp ほど大きくないが、無駄な PI を残さないのが正しい。
  const creatingRef = useRef(false)

  useEffect(() => {
    if (!state.amount) return
    if (creatingRef.current) return
    creatingRef.current = true
    ;(async () => {
      try {
        // Phase 3 では mode 引数を直接 API に渡す (useStore.createChargeIntent は
        // mock/on-chain 用のシグネチャで LPM 経路と噛み合わないため使わない)。
        const res = await api.createChargeIntent(state.amount!, 'local_payment')
        if (!res || !res.payment_intent_client_secret) {
          throw new Error(t('chargeLocal.errorNoClientSecret'))
        }
        setIntent(res)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setLoading(false)
      }
    })()
    // createChargeIntent は setter で参照だけあれば十分 (関数 identity が変わる度に
    // effect 再実行される問題を回避、creatingRef guard で二重発火は防いでる)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.amount])

  const elementsOptions = useMemo(() => {
    if (!intent?.payment_intent_client_secret) return null
    return {
      clientSecret: intent.payment_intent_client_secret,
      appearance: {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: '#C0392B',
          borderRadius: '12px',
          fontFamily: '"Inter", "Noto Sans JP", system-ui, sans-serif',
        },
      },
    }
  }, [intent?.payment_intent_client_secret])

  if (!state.amount) {
    return <Navigate to="/charge" replace />
  }

  return (
    <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      <Header title={t('chargeLocal.title')} onBack={() => navigate(-1)} />

      <div className="mx-4 mt-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
        <p className="font-body text-xs text-yellow-800 leading-5">
          {t('chargeLocal.methodNote')}
        </p>
      </div>

      {loading && (
        <div className="p-6 text-center font-body text-sm text-text-secondary">
          {t('chargeLocal.preparing')}
        </div>
      )}

      {error && (
        <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="font-body text-sm text-red-600">
            {t('chargeLocal.errorLoad')}
          </p>
          <p className="font-body text-xs text-red-500 mt-1 break-all">{error}</p>
        </div>
      )}

      {intent && elementsOptions && (
        <div className="flex-1">
          <Elements stripe={stripePromise} options={elementsOptions}>
            <PaymentForm
              chargeId={intent.charge_id}
              totalJpy={intent.total_jpy}
              amountJpy={intent.amount_jpy}
              feeJpy={intent.fee_jpy}
            />
          </Elements>
        </div>
      )}
    </div>
  )
}
